import { DynamicIcon } from 'lucide-react/dynamic'
import { normalizeIconName } from '@/components/app/pickers/icon-picker'
import { DEFAULT_PROJECT_ICON } from '@/lib/constants/icons'
import { cn } from '@/lib/utils'

export type ProjectIconType = 'icon' | 'emoji' | 'custom'

export interface ParsedProjectIcon {
  type: ProjectIconType
  value: string
}

/**
 * Parse iconType + icon fields into a structured representation.
 * Backwards compatible: if iconType is null but icon has a value, treat as Lucide icon.
 */
export function parseProjectIcon(
  iconType: string | null | undefined,
  icon: string | null | undefined,
): ParsedProjectIcon | null {
  if (!icon) return null

  if (iconType === 'emoji') return { type: 'emoji', value: icon }
  if (iconType === 'custom') return { type: 'custom', value: icon }

  // Default: treat as Lucide icon (handles iconType === 'icon' and legacy null iconType)
  return { type: 'icon', value: icon }
}

/**
 * The icon onboarding gives a user's first Space (moly-platform
 * `AuthenticationController`: Lucide `house` on #3B82F6). It is a default the
 * user never picked, and it is Home's glyph, so in the sidebar rail it read as
 * a second Home (MOL-42). Badge surfaces that know the Space's name show its
 * initial instead. Any other glyph or colour counts as an explicit choice and
 * is kept.
 */
const SEEDED_SPACE_ICONS = new Set(['house', 'home'])
const SEEDED_SPACE_ICON_COLOR = '#3b82f6'

export function isSeededSpaceIcon(
  iconType: string | null | undefined,
  icon: string | null | undefined,
  iconColor: string | null | undefined,
): boolean {
  return (
    (iconType == null || iconType === 'icon') &&
    !!icon &&
    SEEDED_SPACE_ICONS.has(icon.trim().toLowerCase()) &&
    iconColor?.trim().toLowerCase() === SEEDED_SPACE_ICON_COLOR
  )
}

interface ProjectIconProps {
  iconType: string | null | undefined
  icon: string | null | undefined
  iconColor?: string | null
  className?: string
  size?: number
  /**
   * 'badge' renders a filled colored square with the entity's initial as the
   * fallback when no icon is set (the "Clarified tree" project badge). Any set
   * icon (emoji / image / Lucide) is still rendered unchanged. Defaults to
   * 'plain', which keeps the legacy generic-outline fallback. With a
   * `fallbackLabel`, the onboarding-default Space icon also falls back to the
   * initial (see `isSeededSpaceIcon`).
   */
  variant?: 'plain' | 'badge'
  /** Text the badge fallback derives its initial from (e.g. project name). */
  fallbackLabel?: string
  /**
   * Override the Lucide/letter glyph size inside a colored badge. Defaults to
   * `Math.round(size * 0.72)`. Use this when the outer badge must stay compact
   * but the glyph should match a neighboring outline icon (e.g. collapsed rail).
   */
  badgeIconSize?: number
}

/** Stable, varied badge colors when a project has no explicit iconColor. */
// Non-empty tuple: the modulo index is still `string | undefined` to TS, so
// element 0 serves as the fallback.
const BADGE_PALETTE: [string, ...Array<string>] = [
  '#F59E0B', // amber
  '#8B5CF6', // violet
  '#10B981', // emerald
  '#EC4899', // pink
  '#3B82F6', // blue
  '#F97316', // orange
  '#14B8A6', // teal
  '#EF4444', // red
]

function badgeColorFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return BADGE_PALETTE[Math.abs(hash) % BADGE_PALETTE.length] ?? BADGE_PALETTE[0]
}

/** A project's identifying colour: its chosen iconColor, else a stable pick. */
export function projectAccentColor(project: { iconColor: string | null; name: string }): string {
  return project.iconColor || badgeColorFor(project.name.trim() || '?')
}

/**
 * Renders a project icon based on its type (emoji, Lucide icon, or custom image).
 * When iconColor is provided and icon type is 'icon' (Lucide), applies the color.
 */
export function ProjectIcon({
  iconType,
  icon,
  iconColor,
  className,
  size = 16,
  variant = 'plain',
  fallbackLabel,
  badgeIconSize,
}: ProjectIconProps) {
  const seededDefault =
    variant === 'badge' && !!fallbackLabel?.trim() && isSeededSpaceIcon(iconType, icon, iconColor)
  const parsed = seededDefault ? null : parseProjectIcon(iconType, icon)
  const glyphSize = badgeIconSize ?? Math.round(size * 0.72)

  if (!parsed) {
    // Filled letter badge — fallback only, shown when no icon has been chosen.
    if (variant === 'badge') {
      const seed = fallbackLabel?.trim() || '?'
      const initial = seed.charAt(0).toUpperCase()
      const badgeSize = size + 4
      return (
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-[5px] font-semibold leading-none text-white',
            className,
          )}
          style={{
            width: badgeSize,
            height: badgeSize,
            backgroundColor: iconColor || badgeColorFor(seed),
            fontSize: glyphSize,
          }}
          aria-hidden
        >
          {initial}
        </span>
      )
    }

    // Render default icon
    return (
      <DynamicIcon
        name={DEFAULT_PROJECT_ICON}
        className={cn('shrink-0', className)}
        size={size}
      />
    )
  }

  switch (parsed.type) {
    case 'emoji':
      return (
        <span
          className={cn('shrink-0 leading-none text-center', className)}
          style={{ fontSize: size }}
          role="img"
          aria-label="Project icon"
        >
          {parsed.value}
        </span>
      )

    case 'custom':
      return (
        <img
          src={parsed.value}
          alt="Project icon"
          className={cn('shrink-0 rounded object-cover', className)}
          style={{ width: size, height: size }}
        />
      )

    case 'icon':
    default: {
      const iconName = normalizeIconName(parsed.value) ?? DEFAULT_PROJECT_ICON

      // When a color is chosen it fills the badge *background*, with the symbol
      // rendered on top in white — matching the letter-badge fallback above.
      // No color → plain icon using the surrounding foreground color.
      if (iconColor) {
        const badgeSize = size + 4
        return (
          <span
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-[5px] text-white',
              className,
            )}
            style={{ width: badgeSize, height: badgeSize, backgroundColor: iconColor }}
            aria-hidden
          >
            <DynamicIcon name={iconName} size={glyphSize} />
          </span>
        )
      }

      return (
        <DynamicIcon
          name={iconName}
          className={cn('shrink-0', className)}
          size={size}
        />
      )
    }
  }
}
